import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useState } from 'react';
import { DiceError, SheetEvaluator, describeRoll, fieldFor, secureRandom, type CharacterSheet } from '@realmery/realm-core';
import { realms } from '../../db/client.ts';
import { realmFile, type SheetView } from '../../db/protocol.ts';
import { recordRoll } from '../../dice/diceStore.ts';
import { NewSheet } from './NewSheet.tsx';
import { message } from './SheetsPage.tsx';

/**
 * An entry's Character sheet section, as the desktop's: for a Person or Creature, or any
 * entry that already has a sheet. Its template and summary, Open sheet, a quick initiative
 * roll and Unlink - or, with no sheet yet, Create sheet... and Link existing....
 */
export function EntrySheet({ realmKey, entry }: { realmKey: string; entry: { id: string; title: string; entityType: string } }) {
  const file = realmFile(realmKey);
  const navigate = useNavigate();
  const character = entry.entityType === 'PERSON' || entry.entityType === 'CREATURE';
  const [view, setView] = useState<SheetView | null | undefined>(undefined);
  const [panel, setPanel] = useState<'none' | 'new' | 'link'>('none');
  const [rolled, setRolled] = useState<{ text: string; title: string } | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(() => realms.entrySheet(file, entry.id).then(setView), [file, entry.id]);
  useEffect(() => {
    void load();
  }, [load]);

  if (view === undefined || (!character && view === null)) return null;

  async function act(work: () => Promise<void>, fallback: string) {
    setError('');
    try {
      await work();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  function rollInitiative(sheet: CharacterSheet, current: SheetView) {
    try {
      const result = new SheetEvaluator(current.template, sheet.values).rollInitiative(secureRandom);
      if (!result) return;
      recordRoll(`${sheet.name}: Initiative`, result);
      setRolled({ text: String(result.total), title: describeRoll(result) });
    } catch (e) {
      if (!(e instanceof DiceError)) throw e;
      setRolled({ text: '!', title: e.message });
    }
  }

  const openSheet = (sheetId: string) => void navigate({ to: '/realm/$realmKey/sheets/$sheetId', params: { realmKey, sheetId } });

  return (
    <section className="entry-sheet" aria-label="Character sheet">
      <h2>Character sheet</h2>
      {view ? (
        <>
          <p className="meta">
            {view.template.name}
            {view.template.system ? ` · ${view.template.system}` : ''}
          </p>
          <p className="sheet-summary">{new SheetEvaluator(view.template, view.sheet.values).summary() || view.sheet.name}</p>
          <div className="actions">
            <Link className="button primary" to="/realm/$realmKey/sheets/$sheetId" params={{ realmKey, sheetId: view.sheet.id }}>
              Open sheet
            </Link>
            <button type="button" title="Roll initiative from the sheet" disabled={!fieldFor(view.template, 'INITIATIVE')} onClick={() => rollInitiative(view.sheet, view)}>
              🎲 Initiative
            </button>
            {rolled && (
              <output className="sheet-roll-result" title={rolled.title} aria-label={`Initiative: ${rolled.title}`}>
                {rolled.text}
              </output>
            )}
            <button
              type="button"
              className="quiet"
              title="Keep the sheet, but not as this entry's"
              onClick={() =>
                void act(async () => {
                  await realms.linkSheet(file, view.sheet.id, null);
                  setRolled(null);
                  await load();
                }, 'The sheet could not be unlinked.')
              }
            >
              Unlink
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="meta">No character sheet yet.</p>
          <div className="actions">
            <button type="button" title="Make a character sheet for this entry from a template" onClick={() => setPanel(panel === 'new' ? 'none' : 'new')}>
              Create sheet…
            </button>
            <button type="button" title="Make a sheet that isn't anyone's yet this entry's sheet" onClick={() => setPanel(panel === 'link' ? 'none' : 'link')}>
              Link existing…
            </button>
          </div>
          {panel === 'new' && <NewSheet file={file} forEntry={{ id: entry.id, title: entry.title }} onCreated={openSheet} onCancel={() => setPanel('none')} />}
          {panel === 'link' && (
            <LinkExisting
              file={file}
              entryTitle={entry.title}
              onCancel={() => setPanel('none')}
              onLink={(sheetId) =>
                act(async () => {
                  await realms.linkSheet(file, sheetId, entry.id);
                  setPanel('none');
                  await load();
                }, 'The sheet could not be linked.')
              }
            />
          )}
        </>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** Which unlinked sheet is this entry's; one named as the entry is chosen. */
function LinkExisting({ file, entryTitle, onLink, onCancel }: { file: string; entryTitle: string; onLink: (sheetId: string) => Promise<void>; onCancel: () => void }) {
  const [sheets, setSheets] = useState<CharacterSheet[] | null>(null);
  const [chosen, setChosen] = useState('');
  const id = useId();
  useEffect(() => {
    let live = true;
    void realms.sheetsOverview(file).then(({ sheets: all }) => {
      if (!live) return;
      const unlinked = all.filter((s) => s.entryId === null);
      setSheets(unlinked);
      setChosen(unlinked.find((s) => s.name.toLowerCase() === entryTitle.toLowerCase())?.id ?? '');
    });
    return () => {
      live = false;
    };
  }, [file, entryTitle]);
  return (
    <form
      className="panel"
      aria-label="Link an existing sheet"
      onSubmit={(e) => {
        e.preventDefault();
        if (chosen) void onLink(chosen);
      }}
    >
      {sheets !== null && sheets.length === 0 ? (
        <p className="meta">Every sheet already belongs to an entry.</p>
      ) : (
        <>
          <label htmlFor={id}>Which sheet is {entryTitle}'s?</label>
          <select id={id} value={chosen} disabled={sheets === null} onChange={(e) => setChosen(e.currentTarget.value)}>
            <option value="">(Choose a sheet)</option>
            {(sheets ?? []).map((sheet) => (
              <option key={sheet.id} value={sheet.id}>
                {sheet.name}
              </option>
            ))}
          </select>
        </>
      )}
      <div className="actions">
        <button type="submit" className="primary" disabled={!chosen}>
          Link
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
